/**
 * Error Helpers
 * Utilities for normalizing error objects to display-friendly strings
 */

/**
 * Normalize any error type to a string for safe React rendering
 * @param {any} error - Error object, string, or unknown type
 * @returns {string} - Human-readable error message
 */
export const normalizeError = (error) => {
  // Якщо це вже рядок
  if (typeof error === 'string') {
    return error
  }

  // Якщо null або undefined
  if (!error) {
    return 'An unknown error occurred'
  }

  // Якщо це об'єкт помилки з backend
  if (typeof error === 'object') {
    // Backend error structure: { name, errors: [{code, message}] }
    if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
      return error.errors.map(e => e.message || e.code || 'Error').join('; ')
    }

    // Forbidden/NotAuthorized carry no `errors` array, so the API serialises them as
    // { name: 'Server error', errors: '<the actual message>' }. Without this the `error.name`
    // fallback below wins and the user is shown the literal string "Server error".
    if (typeof error.errors === 'string' && error.errors.trim()) {
      return error.errors
    }

    // Axios error structure: { response: { data: { error: {...} } } }
    if (error.response?.data?.error) {
      // A 5xx body is not written for the user: the API serialises any unhandled error as
      // { name: 'Server error', errors: err.message } — a Mongoose cast error, a driver string,
      // whatever reached it — and the string branch above would render that verbatim in a toast.
      if (error.response.status >= 500) {
        return 'Something went wrong. Please try again later.'
      }
      return normalizeError(error.response.data.error)
    }

    // Standard Error object з message
    if (error.message) {
      return error.message
    }

    // Error object з name
    if (error.name) {
      return error.name
    }

    // Fallback для невідомих об'єктів
    try {
      return JSON.stringify(error)
    } catch (e) {
      return 'An error occurred'
    }
  }

  return 'An unknown error occurred'
}

/**
 * Extract error message from Redux error payload
 * @param {any} errorPayload - Redux action.payload from rejected thunk
 * @returns {string} - Error message
 */
export const getErrorMessage = (errorPayload) => {
  return normalizeError(errorPayload)
}

// The backend's error envelope has TWO shapes and the old extraction handled neither of the ones
// these endpoints actually produce:
//   * `new RequestError(msg)` leaves `errors` as '' (falsy), so ExpressLoader's array-shaped
//     handler is skipped and the final handler answers { error: { name: 'Server error',
//     errors: '<message string>' } } — `errors` is a STRING.
//   * a role refusal throws Forbidden, which likewise carries no `errors` array, so a 403 arrives
//     in that same string shape.
// The old `error?.errors?.[0]?.message` therefore read the FIRST CHARACTER of the string and then
// `.message` of that, i.e. undefined — so EVERY server message ('Only a processed (cleared) charge
// can be refunded', 'Payment has already been refunded', 'Your account is not permitted to reverse
// a greenMoney payment', a gateway's verbatim rejection) collapsed to the generic fallback.
// Handle both shapes.
export const failureOf = (result, fallback) => {
  const payload = result?.payload
  const usable = typeof payload === 'string' ? payload.trim() : payload && (payload.message || payload.errors)
  return new Error(usable ? normalizeError(payload) : fallback)
}

export const extractApiError = (error, fallback) => {
  const errors = error?.response?.data?.error?.errors
  if (typeof errors === 'string' && errors) return errors
  if (Array.isArray(errors) && errors[0]?.message) return errors[0].message
  return error?.response?.data?.error?.message || fallback
}
